/**
 * After a deploy, a visitor whose page was loaded before it still asks for the old build's files, which
 * no longer exist (Netlify serves each deploy alone). The page then loads the current version once —
 * never in a loop — instead of showing an error. A guest returning from the bank app after paying a Pix
 * is the usual case: the confirmation page reloads with its URL, which carries the payment.
 */
const RELOAD_KEY = 'aquamarina_stale_build_reload';
/** A second failure this soon after reloading is a real problem: show it instead of reloading again. */
const LOOP_GUARD_MS = 20_000;

/** A part of the site that failed to download because the page is older than the last deploy. */
export function isStaleBuildError(error: unknown): boolean {
  const text = `${(error as { name?: string })?.name || ''} ${(error as Error)?.message || error}`;
  return /dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS|ChunkLoadError/i.test(text);
}

/** Reloads into the current version; false when it already did so a moment ago. */
export function reloadIntoCurrentVersion(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last < LOOP_GUARD_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // Storage blocked: reloading once is still right; a loop cannot be ruled out, so it is rare enough.
  }
  window.location.reload();
  return true;
}
